import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useSettings } from "../../contexts/SettingsContext";

/**
 * Mask a Razorpay Key ID for read-only display.
 * Input  : "rzp_live_ABCD1234EFGH5678"
 * Output : "rzp_live_ABCD********5678"
 *
 * Rules (per iter48 spec):
 *   - keep "rzp_live_" or "rzp_test_" prefix verbatim
 *   - keep the first 4 chars after the prefix
 *   - keep the last 4 chars
 *   - replace everything in between with "*" (variable length stars)
 *
 * Returns "—" when no key is configured (empty env var on the backend).
 */
function maskRazorpayKeyId(raw) {
  if (!raw || typeof raw !== "string") return "—";
  const m = raw.match(/^(rzp_(?:live|test)_)(.+)$/);
  if (!m) return raw; // unknown shape — display as-is rather than mangle
  const prefix = m[1];
  const body = m[2];
  if (body.length <= 8) return raw; // too short to mask meaningfully
  const head = body.slice(0, 4);
  const tail = body.slice(-4);
  const starLen = Math.max(4, body.length - 8);
  return `${prefix}${head}${"*".repeat(starLen)}${tail}`;
}

export default function AdminSettings() {
  const [s, setS] = useState({
    whatsapp_number: "",
    contact_number: "",
    contact_email: "",
    business_hours: "",
    free_delivery_threshold: 999,
    razorpay_key_id: "",
    min_order_amount: 200,
    product_card_slideshow_enabled: true,
    product_card_transition_type: "scale_kenburns",
    free_delivery_enabled: false,
    order_cancellation_window_enabled: false,
    // Session L / iter109 — dynamic Charges list (Handling Fee, Packaging, …).
    // Each entry: { id, name, amount, active }.  Admin can rename / edit
    // amount / toggle active / add / remove.  Historical orders keep
    // their own snapshot; edits only affect FUTURE orders.
    charges: [],
  });
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { refresh } = useSettings();

  useEffect(() => {
    api.get("/settings").then((r) => setS({
      whatsapp_number: r.data.whatsapp_number || "",
      contact_number: r.data.contact_number || "",
      contact_email: r.data.contact_email || "sridurgafoodssupport@gmail.com",
      business_hours: r.data.business_hours || "Mon – Sat | 10:00 AM – 6:00 PM",
      free_delivery_threshold: r.data.free_delivery_threshold || 999,
      razorpay_key_id: r.data.razorpay_key_id || "",
      min_order_amount: r.data.min_order_amount || 200,
      product_card_slideshow_enabled: r.data.product_card_slideshow_enabled !== false,
      product_card_transition_type: r.data.product_card_transition_type || "scale_kenburns",
      free_delivery_enabled: !!r.data.free_delivery_enabled,
      order_cancellation_window_enabled: !!r.data.order_cancellation_window_enabled,
      charges: Array.isArray(r.data.charges) && r.data.charges.length > 0
        ? r.data.charges
        : [{ id: "handling_fee", name: "Handling Fee", amount: r.data.handling_fee_amount ?? 6, active: true }],
    }));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS({
        whatsapp_number: data.whatsapp_number,
        contact_number: data.contact_number || "",
        contact_email: data.contact_email || "sridurgafoodssupport@gmail.com",
        business_hours: data.business_hours || "Mon – Sat | 10:00 AM – 6:00 PM",
        free_delivery_threshold: data.free_delivery_threshold,
        razorpay_key_id: data.razorpay_key_id,
        min_order_amount: data.min_order_amount,
        product_card_slideshow_enabled: data.product_card_slideshow_enabled !== false,
        product_card_transition_type: data.product_card_transition_type || "scale_kenburns",
        free_delivery_enabled: !!data.free_delivery_enabled,
        order_cancellation_window_enabled: !!data.order_cancellation_window_enabled,
        charges: Array.isArray(data.charges) && data.charges.length > 0
          ? data.charges
          : [{ id: "handling_fee", name: "Handling Fee", amount: data.handling_fee_amount ?? 6, active: true }],
      });
      // Broadcast new threshold to the whole storefront immediately.
      await refresh();
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif text-4xl text-brand-dark mb-8">Settings</h1>
      <form onSubmit={save} className="bg-white border border-brand-line rounded-2xl p-6 space-y-5 max-w-2xl">
        <div>
          <label className="label-field">WhatsApp notification number</label>
          <input className="input-field" value={s.whatsapp_number || ""} onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })} placeholder="+919999999999" data-testid="settings-whatsapp" />
          <p className="text-xs text-brand-muted mt-1.5">Order details are logged here on every paid order. Number is editable any time.</p>
        </div>
        <div>
          <label className="label-field">Contact number</label>
          <input className="input-field" value={s.contact_number || ""} onChange={(e) => setS({ ...s, contact_number: e.target.value })} placeholder="+919999999999" data-testid="settings-contact-number" />
          <p className="text-xs text-brand-muted mt-1.5">Public-facing phone number — displayed in the footer, policy pages and contact CTAs. Updates everywhere instantly when saved.</p>
        </div>
        <div>
          <label className="label-field">Customer contact email</label>
          <input type="email" className="input-field" value={s.contact_email || ""} onChange={(e) => setS({ ...s, contact_email: e.target.value })} placeholder="sridurgafoodssupport@gmail.com" data-testid="settings-contact-email" />
          <p className="text-xs text-brand-muted mt-1.5">Public-facing customer email — displayed in the Homepage &quot;Get in touch&quot; section, footer, Contact Us page and policy pages. Updates everywhere instantly when saved.</p>
        </div>
        <div>
          <label className="label-field">Business hours</label>
          <input className="input-field" value={s.business_hours || ""} onChange={(e) => setS({ ...s, business_hours: e.target.value })} placeholder="Mon – Sat | 10:00 AM – 6:00 PM" data-testid="settings-business-hours" />
          <p className="text-xs text-brand-muted mt-1.5">Displayed as a small line below the contact details in the footer and Contact Us page. Free-text — enter any format you prefer.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-field">Free delivery above ₹</label>
            <input type="number" className="input-field" value={s.free_delivery_threshold} onChange={(e) => setS({ ...s, free_delivery_threshold: parseFloat(e.target.value) })} data-testid="settings-threshold" />
            <p className="text-xs text-brand-muted mt-1.5">Shipping cost is fetched live from Shiprocket for every order. Only orders at or above this threshold ship free — and only when the free-delivery toggle below is ON.</p>
          </div>
          <div>
            <label className="label-field">Minimum ordering amount ₹</label>
            <input type="number" className="input-field" value={s.min_order_amount} onChange={(e) => setS({ ...s, min_order_amount: parseFloat(e.target.value) })} data-testid="settings-min-order" />
          </div>
        </div>

        {/* Session L / iter109 — dynamic Charges section.  Directly
             below Minimum Ordering Amount as requested.  Handling Fee
             (id="handling_fee") is the seeded first entry; admin can
             rename it, edit the amount, add sibling charges, or toggle
             the "active" flag.  A hidden charge does NOT get applied
             to future orders. */}
        <div className="border-t border-brand-line pt-5" data-testid="settings-charges-section">
          <h2 className="font-serif text-xl text-brand-dark mb-1">Charges</h2>
          <p className="text-xs text-brand-muted mb-3">
            Extra charges applied per order (e.g. Handling Fee, Packaging Charge).
            Edits only affect FUTURE orders — historical orders keep the charges
            that were active when they were placed.
          </p>
          <div className="space-y-3">
            {(s.charges || []).map((c, i) => (
              <div key={c.id || i}
                   className="grid grid-cols-12 gap-2 items-center bg-brand-subtle/30 rounded-xl p-3 border border-brand-line"
                   data-testid={`charge-row-${i}`}>
                <input
                  className="input-field col-span-6"
                  placeholder="Charge name"
                  value={c.name || ""}
                  onChange={(e) => {
                    const next = [...s.charges];
                    next[i] = { ...next[i], name: e.target.value };
                    setS({ ...s, charges: next });
                  }}
                  data-testid={`charge-name-${i}`}
                />
                <div className="col-span-3 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="input-field !pl-7"
                    placeholder="0.00"
                    value={c.amount ?? ""}
                    onChange={(e) => {
                      const next = [...s.charges];
                      next[i] = { ...next[i], amount: e.target.value === "" ? 0 : parseFloat(e.target.value) };
                      setS({ ...s, charges: next });
                    }}
                    data-testid={`charge-amount-${i}`}
                  />
                </div>
                <label className="col-span-2 text-xs flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-brand-chili"
                    checked={c.active !== false}
                    onChange={(e) => {
                      const next = [...s.charges];
                      next[i] = { ...next[i], active: e.target.checked };
                      setS({ ...s, charges: next });
                    }}
                    data-testid={`charge-active-${i}`}
                  />
                  Active
                </label>
                <button
                  type="button"
                  onClick={() => {
                    // Handling Fee is the seeded row — protect it from
                    // deletion so historical footers keep resolving.
                    if (c.id === "handling_fee") {
                      toast.error("Handling Fee cannot be deleted — you can rename it or set amount to 0.");
                      return;
                    }
                    setS({ ...s, charges: s.charges.filter((_, idx) => idx !== i) });
                  }}
                  className="col-span-1 text-brand-muted hover:text-brand-chili text-sm p-1"
                  title="Remove charge"
                  data-testid={`charge-remove-${i}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setS({
              ...s,
              charges: [
                ...(s.charges || []),
                { id: "", name: "", amount: 0, active: true },
              ],
            })}
            className="mt-3 text-sm text-brand-chili font-semibold"
            data-testid="charge-add-btn"
          >
            + Add New Charge
          </button>
        </div>
        <div>
          <label className="label-field">Razorpay Key ID (read-only)</label>
          <input
            className="input-field bg-brand-cream/40 cursor-not-allowed font-mono text-sm"
            value={maskRazorpayKeyId(s.razorpay_key_id)}
            readOnly
            disabled
            tabIndex={-1}
            data-testid="settings-razorpay-masked"
          />
          <p className="text-xs text-brand-muted mt-1.5">
            This value is automatically read from the production deployment environment variables and cannot be edited from the admin dashboard.
          </p>
        </div>
        <div className="border-t border-brand-line pt-5">
          <h2 className="font-serif text-xl text-brand-dark mb-3" data-testid="settings-slideshow-heading">Product Card Variant Transition</h2>
          <label className="flex items-start justify-between gap-4 cursor-pointer">
            <span className="text-sm text-brand-dark flex-1 leading-relaxed">
              Toggle automatic variant transitions for product cards across Crowd Favourites and Collections.
              <span className="block text-xs text-brand-muted mt-0.5">When ON, product cards auto-cycle through each variant&apos;s image using the selected animation; OFF shows only the main image.</span>
            </span>
            <input
              type="checkbox"
              checked={s.product_card_slideshow_enabled}
              onChange={(e) => setS({ ...s, product_card_slideshow_enabled: e.target.checked })}
              className="w-6 h-6 accent-brand-chili mt-1 cursor-pointer"
              data-testid="settings-slideshow-toggle"
            />
          </label>
          <div className="mt-4">
            <label className="label-field">Transition style</label>
            <select
              className="input-field"
              value={s.product_card_transition_type}
              onChange={(e) => setS({ ...s, product_card_transition_type: e.target.value })}
              disabled={!s.product_card_slideshow_enabled}
              data-testid="settings-transition-type"
            >
              <option value="slideshow">Slideshow (Original)</option>
              <option value="scale">Scale Transition</option>
              <option value="scale_kenburns">Scale Transition + Ken Burns</option>
              <option value="coverflow">3D Cover Flow Carousel</option>
            </select>
            <p className="text-xs text-brand-muted mt-1.5">Only one transition runs at a time. Default for this preview: Scale Transition + Ken Burns.</p>
          </div>
        </div>

        <div className="border-t border-brand-line pt-5">
          <h2 className="font-serif text-xl text-brand-dark mb-3" data-testid="settings-free-delivery-heading">Free Delivery</h2>
          <label className="flex items-start justify-between gap-4 cursor-pointer">
            <span className="text-sm text-brand-dark flex-1 leading-relaxed">
              Enable the free-delivery program for orders above the threshold.
              <span className="block text-xs text-brand-muted mt-0.5">When OFF, every order pays the live Shiprocket rate (recommended — varies by pincode and weight). When ON, orders ≥ the threshold above ship for free.</span>
            </span>
            <input
              type="checkbox"
              checked={!!s.free_delivery_enabled}
              onChange={(e) => setS({ ...s, free_delivery_enabled: e.target.checked })}
              className="w-6 h-6 accent-brand-chili mt-1 cursor-pointer"
              data-testid="settings-free-delivery-toggle"
            />
          </label>
        </div>

        <div className="border-t border-brand-line pt-5">
          <h2 className="font-serif text-xl text-brand-dark mb-3" data-testid="settings-cancellation-heading">Order Cancellation Window</h2>
          <label className="flex items-start justify-between gap-4 cursor-pointer">
            <span className="text-sm text-brand-dark flex-1 leading-relaxed">
              Enable the 30-minute cancellation timer for customers.
              <span className="block text-xs text-brand-muted mt-0.5">When OFF, customers can cancel any order before dispatch — no countdown shown. When ON, a 30-minute timer appears on the Order Placed page and in the customer&apos;s Orders list.</span>
            </span>
            <input
              type="checkbox"
              checked={!!s.order_cancellation_window_enabled}
              onChange={(e) => setS({ ...s, order_cancellation_window_enabled: e.target.checked })}
              className="w-6 h-6 accent-brand-chili mt-1 cursor-pointer"
              data-testid="settings-cancellation-toggle"
            />
          </label>
        </div>

        <button type="submit" disabled={loading} className="btn-primary" data-testid="settings-save-btn">{loading ? "Saving…" : "Save changes"}</button>
      </form>

      <ShiprocketPickupCard />

      <PaymentOrderSettingsCard />

      <PackagingTiersCard />

      {/* iter55 — one-time factory reset zone. Wipes all orders +
          non-admin users + payment intents + Meta CAPI log. Admin auth +
          two-step confirmation guard the call so it can never be triggered
          by accident. Designed for launch-day cleanup of test data. */}
      <div className="mt-12 bg-white border-2 border-red-200 rounded-2xl p-6 max-w-2xl">
        <h2 className="font-serif text-xl text-red-700 mb-2">Danger zone · Factory reset</h2>
        <p className="text-sm text-brand-muted leading-relaxed mb-4">
          Permanently delete every order, every non-admin user, every pending payment intent, and the Meta CAPI debug log. Admin accounts are preserved. Use this once after launch to clear test data — there is no undo.
        </p>
        <button
          type="button"
          data-testid="settings-factory-reset-btn"
          disabled={resetting}
          onClick={async () => {
            const phrase = window.prompt(
              "This will permanently delete ALL orders and all non-admin customer accounts.\n\nType DELETE to confirm:",
            );
            if (phrase !== "DELETE") {
              toast.message("Factory reset cancelled.");
              return;
            }
            setResetting(true);
            try {
              const { data } = await api.post("/admin/factory-reset?confirm=DELETE_ALL_TEST_DATA");
              toast.success(
                `Reset complete — deleted ${data.deleted.orders} orders, ${data.deleted.non_admin_users} customers. Orders: ${data.remaining.orders}. Customers: ${data.remaining.customers}.`,
                { duration: 8000 },
              );
            } catch (err) {
              toast.error(formatApiError(err));
            } finally {
              setResetting(false);
            }
          }}
          className="px-5 py-2.5 rounded-full border-2 border-red-600 text-red-700 hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {resetting ? "Resetting…" : "Reset production data"}
        </button>
      </div>
    </div>
  );
}

/**
 * iter78 — Shiprocket pickup-location picker.
 * Fetches every pickup location that currently exists in the connected
 * Shiprocket account, shows the currently-active nickname, and lets the
 * admin switch to another with a single Save.  The website starts using
 * the newly-selected pickup for every subsequent order + shipping quote
 * immediately — no redeploy, no env-var edits.
 */
function ShiprocketPickupCard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [current, setCurrent] = useState("");
  const [selected, setSelected] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/shiprocket/pickup-locations");
      setLocations(data.locations || []);
      setCurrent(data.current || "");
      setSelected(data.current || "");
    } catch (err) {
      setLocations([]);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    toast.success("Synced pickup locations from Shiprocket.");
  };

  const save = async () => {
    if (!selected || selected === current) {
      toast.message("Nothing to save — pickup location is unchanged.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/shiprocket/pickup-location", { pickup_location: selected });
      setCurrent(data.pickup_location);
      toast.success(`Shipping now originates from "${data.pickup_location}"${data.pin_code ? ` · PIN ${data.pin_code}` : ""}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const activeMeta = locations.find((l) => l.pickup_location === selected);

  return (
    <div className="mt-8 bg-white border border-brand-line rounded-2xl p-6 max-w-2xl space-y-4" data-testid="shiprocket-pickup-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-brand-dark">Shiprocket Configuration</h2>
          <p className="text-xs text-brand-muted mt-1 leading-relaxed">
            Selects which pickup warehouse in your Shiprocket account is used
            when the website creates a shipment. Whenever you add or remove a
            pickup location in Shiprocket, click <b>Sync</b> below and pick it
            here — no code changes required.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="btn-secondary text-xs disabled:opacity-60"
          data-testid="shiprocket-pickup-sync"
        >
          {refreshing ? "Syncing…" : "Sync from Shiprocket"}
        </button>
      </div>

      {loading && <div className="text-sm text-brand-muted">Loading pickup locations…</div>}

      {error && (
        <div className="text-sm text-brand-chili bg-brand-chili/5 border border-brand-chili/20 rounded-lg px-3 py-2" data-testid="shiprocket-pickup-error">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div>
            <label className="label-field">Active pickup location</label>
            <select
              className="input-field"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={locations.length === 0}
              data-testid="shiprocket-pickup-select"
            >
              {locations.length === 0 ? (
                <option value="">No pickup locations found in Shiprocket</option>
              ) : (
                <>
                  <option value="">Select a pickup location</option>
                  {locations.map((l) => (
                    <option key={l.pickup_location} value={l.pickup_location}>
                      {l.pickup_location}{l.city ? ` · ${l.city}` : ""}{l.pin_code ? ` · ${l.pin_code}` : ""}
                    </option>
                  ))}
                </>
              )}
            </select>
            <p className="text-xs text-brand-muted mt-1.5">
              Currently active: <b>{current || "— not selected —"}</b>
            </p>
          </div>

          {activeMeta && (
            <div className="text-xs text-brand-muted bg-brand-subtle rounded-lg p-3 space-y-0.5" data-testid="shiprocket-pickup-preview">
              <div><b>Contact:</b> {activeMeta.name || "—"}</div>
              <div><b>Address:</b> {[activeMeta.address, activeMeta.address_2].filter(Boolean).join(", ") || "—"}</div>
              <div><b>City / State:</b> {activeMeta.city || "—"}, {activeMeta.state || "—"}</div>
              <div><b>Pincode:</b> {activeMeta.pin_code || "—"}</div>
              <div><b>Phone:</b> {activeMeta.phone || "—"}</div>
            </div>
          )}

          <button
            type="button"
            onClick={save}
            disabled={saving || !selected || selected === current}
            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="shiprocket-pickup-save"
          >
            {saving ? "Saving…" : "Save pickup location"}
          </button>
        </>
      )}
    </div>
  );
}



/**
 * iter94 / Session B — Shiprocket packaging-weight tier editor.
 *
 * Lets the admin define one or more tiers that map an order's CONTENTS
 * weight to a packaging weight (kg).  Tiers are sorted ascending by
 * ``max_contents_kg``; the first tier whose upper bound is >= the order
 * weight supplies the packaging weight.  Leaving the largest tier's
 * upper bound BLANK treats it as "everything heavier than the previous
 * tier".
 *
 * When the admin has NOT configured any tier, the backend falls back to
 * a conservative 0.05 kg buffer (see ``_select_packaging_weight_kg`` in
 * server.py).
 */
function PackagingTiersCard() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (cancelled) return;
        setTiers(data.shiprocket_packaging_tiers || []);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (idx, patch) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const addTier = () => {
    setTiers((prev) => [...prev, { label: "", max_contents_kg: null, packaging_weight_kg: 0 }]);
  };

  const removeTier = (idx) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  const prefillSuggested = () => {
    setTiers([
      { label: "Small",       max_contents_kg: 0.5, packaging_weight_kg: 0 },
      { label: "Medium",      max_contents_kg: 2.0, packaging_weight_kg: 0 },
      { label: "Large",       max_contents_kg: 5.0, packaging_weight_kg: 0 },
      { label: "Very large",  max_contents_kg: null, packaging_weight_kg: 0 },
    ]);
    toast.message("Suggested tier shell added. Enter your actual packaging weights and Save.");
  };

  const save = async () => {
    setSaving(true);
    try {
      // Normalise: coerce numeric fields, blank max_contents_kg → null.
      const clean = tiers.map((t) => ({
        label: (t.label || "").trim(),
        max_contents_kg:
          t.max_contents_kg === "" || t.max_contents_kg === null || t.max_contents_kg === undefined
            ? null
            : Number(t.max_contents_kg),
        packaging_weight_kg: Number(t.packaging_weight_kg || 0),
      }));
      await api.put("/admin/settings", { shiprocket_packaging_tiers: clean });
      setTiers(clean);
      toast.success("Packaging tiers saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mt-8 bg-white border border-brand-line rounded-2xl p-6 max-w-2xl"
      data-testid="packaging-tiers-card"
    >
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h2 className="font-serif text-xl text-brand-dark">Shiprocket Packaging Weight</h2>
          <p className="text-xs text-brand-muted mt-1 leading-relaxed max-w-lg">
            The weight sent to Shiprocket is <b>product weight + packaging weight</b>. Configure your
            packaging weight per order size below. Tiers are matched by the CONTENTS weight in kg —
            the first tier whose upper bound is greater than or equal to the order weight wins. Leave
            the last tier&apos;s upper bound blank to catch all heavier orders. If no tiers are
            configured, a small 0.05 kg buffer is applied automatically.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-brand-muted">Loading…</div>
      ) : (
        <>
          <div className="space-y-2 mt-4" data-testid="packaging-tiers-list">
            <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-brand-muted">
              <div className="col-span-3">Label</div>
              <div className="col-span-4">Upper bound (kg, blank = ∞)</div>
              <div className="col-span-4">Packaging weight (kg)</div>
              <div className="col-span-1"></div>
            </div>
            {tiers.length === 0 && (
              <div className="text-sm text-brand-muted italic py-2">
                No tiers configured yet — orders currently ship with a 0.05 kg conservative buffer.
              </div>
            )}
            {tiers.map((t, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-2 items-center"
                data-testid={`packaging-tier-row-${i}`}
              >
                <input
                  className="input-field col-span-3 text-sm"
                  placeholder="e.g. Small"
                  value={t.label || ""}
                  onChange={(e) => update(i, { label: e.target.value })}
                  data-testid={`packaging-tier-label-${i}`}
                />
                <input
                  type="number"
                  step="0.01"
                  className="input-field col-span-4 text-sm"
                  placeholder="e.g. 0.5"
                  value={t.max_contents_kg === null || t.max_contents_kg === undefined ? "" : t.max_contents_kg}
                  onChange={(e) => update(i, {
                    max_contents_kg: e.target.value === "" ? null : Number(e.target.value),
                  })}
                  data-testid={`packaging-tier-max-${i}`}
                />
                <input
                  type="number"
                  step="0.001"
                  className="input-field col-span-4 text-sm"
                  placeholder="e.g. 0.15"
                  value={t.packaging_weight_kg ?? 0}
                  onChange={(e) => update(i, { packaging_weight_kg: Number(e.target.value) })}
                  data-testid={`packaging-tier-weight-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeTier(i)}
                  className="col-span-1 text-brand-chili hover:text-red-700 text-lg"
                  aria-label="Remove tier"
                  data-testid={`packaging-tier-remove-${i}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              onClick={addTier}
              className="text-sm px-3 py-1.5 border border-brand-line rounded-full hover:bg-brand-subtle/40"
              data-testid="packaging-tier-add"
            >
              + Add tier
            </button>
            {tiers.length === 0 && (
              <button
                type="button"
                onClick={prefillSuggested}
                className="text-sm px-3 py-1.5 border border-brand-line rounded-full hover:bg-brand-subtle/40"
                data-testid="packaging-tier-prefill"
              >
                Prefill suggested tiers (Small/Medium/Large/Very large)
              </button>
            )}
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="btn-primary text-sm disabled:opacity-60"
              data-testid="packaging-tiers-save"
            >
              {saving ? "Saving…" : "Save packaging tiers"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * iter96 / Session C — Payment & Order Settings.
 *
 * Two independent toggles: (1) Cash on Delivery availability, and (2)
 * customer self-cancellation. Both are backed by persistent DB fields
 * ``cod_enabled`` and ``customer_cancellation_enabled`` — turning either
 * OFF instantly hides the corresponding customer-facing UI AND arms the
 * backend enforcement guard.
 *
 * IMPORTANT: turning "Customer Order Cancellation" OFF does NOT prevent
 * Admin/Super-Admin from cancelling orders through the existing
 * PUT /admin/orders/{id}/status?status=cancelled endpoint (guarded by
 * require_admin).  It only controls CUSTOMER self-cancellation.
 */
function PaymentOrderSettingsCard() {
  const [codEnabled, setCodEnabled] = useState(true);
  const [cancEnabled, setCancEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [savingCod, setSavingCod] = useState(false);
  const [savingCanc, setSavingCanc] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (cancelled) return;
        setCodEnabled(data.cod_enabled !== false);
        setCancEnabled(data.customer_cancellation_enabled !== false);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleCod = async (next) => {
    setSavingCod(true);
    try {
      await api.put("/admin/settings", { cod_enabled: next });
      setCodEnabled(next);
      toast.success(
        next ? "Cash on Delivery enabled successfully" : "Cash on Delivery disabled successfully"
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingCod(false);
    }
  };

  const toggleCanc = async (next) => {
    setSavingCanc(true);
    try {
      await api.put("/admin/settings", { customer_cancellation_enabled: next });
      setCancEnabled(next);
      toast.success(
        next
          ? "Customer order cancellation enabled successfully"
          : "Customer order cancellation disabled successfully"
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingCanc(false);
    }
  };

  return (
    <div
      className="mt-8 bg-white border border-brand-line rounded-2xl p-6 max-w-2xl"
      data-testid="payment-order-settings-card"
    >
      <h2 className="font-serif text-xl text-brand-dark mb-1">Payment &amp; Order Settings</h2>
      <p className="text-xs text-brand-muted mb-4">
        Control which payment options customers can choose and whether they can cancel eligible
        orders themselves. Admin/Super-Admin can always manage orders from Admin → Orders regardless
        of the second toggle.
      </p>

      {loading ? (
        <div className="text-sm text-brand-muted">Loading…</div>
      ) : (
        <div className="space-y-4">
          <SettingToggleRow
            title="Cash on Delivery"
            description="Allow customers to place orders using Cash on Delivery. Turning this off hides the COD option at checkout and rejects any COD orders submitted via the API."
            enabled={codEnabled}
            saving={savingCod}
            onToggle={toggleCod}
            testId="cod-enabled-toggle"
          />
          <div className="border-t border-brand-line" />
          <SettingToggleRow
            title="Customer Order Cancellation"
            description="Allow customers to cancel eligible orders themselves from Your Orders / Order Details before dispatch. Admin cancellation is unaffected."
            enabled={cancEnabled}
            saving={savingCanc}
            onToggle={toggleCanc}
            testId="customer-cancellation-toggle"
          />
        </div>
      )}
    </div>
  );
}

function SettingToggleRow({ title, description, enabled, saving, onToggle, testId }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <div className="font-medium text-brand-dark">{title}</div>
        <p className="text-xs text-brand-muted mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={title}
        disabled={saving}
        onClick={() => onToggle(!enabled)}
        className={
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors " +
          (enabled ? "bg-brand-chili" : "bg-brand-line") +
          (saving ? " opacity-60 cursor-not-allowed" : " cursor-pointer")
        }
        data-testid={testId}
      >
        <span
          className={
            "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform " +
            (enabled ? "translate-x-5" : "translate-x-0.5")
          }
        />
      </button>
    </div>
  );
}

